import { authenticatedFetch, backendUrl } from "@/data/http"
import type {
  BloodGlucoseFormApiDto,
  MedicationLogApiDto,
} from "@/model/BloodGlucoseForm"
import type { BloodGlucoseFormContextApiDto } from "@/model/BloodGlucoseFormContext"
import type {
  BloodGlucoseObservationApiDto,
  BloodGlucoseObservationWithReadingApiDto,
} from "@/model/BloodGlucoseObservation"
import type { BloodGlucoseObservationsApiDto } from "@/model/BloodGlucoseObservations"
import type { QuickCheckReasonApiDto } from "@/model/QuickCheckReason"

// Matches CreateBloodGlucoseFormApiDtoV1 on the backend.
// XOR rule: exactly one of `readingId` or `dateTime` must be set.
// - Contextualizing an existing reading: readingId set, dateTime = null
//   (the reading already carries a recorded time; sending dateTime triggers
//   IllegalReadingIdAndDateTimeCombination on the backend).
// - Manual meal form / quick-check (no reading): readingId = null, dateTime set.
export type CreateBloodGlucoseFormApiDto = {
  context: BloodGlucoseFormContextApiDto
  carbsInGrams: number | null
  dateTime: string | null // ISO 8601 with offset; null when readingId is set
  proteinInGrams: number | null
  mealContents: string | null
  medications: MedicationLogApiDto | null
  notes: string | null
  reasons: QuickCheckReasonApiDto[] | null
  readingId: string | null
}

export const createBloodGlucoseForm = ({
  patientId,
  form,
}: {
  patientId: string
  form: CreateBloodGlucoseFormApiDto
}) =>
  authenticatedFetch<BloodGlucoseFormApiDto>(
    `${backendUrl}/v1/patients/${patientId}/forms/blood-glucose`,
    { method: "POST", body: JSON.stringify(form) },
  )

export const getPatientObservations = ({
  patientId,
  fromIso,
  toIso,
}: {
  patientId: string
  fromIso: string
  toIso: string
}) =>
  authenticatedFetch<BloodGlucoseObservationsApiDto>(
    `${backendUrl}/v1/patients/${patientId}/observations/blood-glucose?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
  )

export const getLastBloodGlucoseObservationWithReading = ({ patientId }: { patientId: string }) =>
  authenticatedFetch<BloodGlucoseObservationWithReadingApiDto>(
    `${backendUrl}/v1/patients/${patientId}/observations/blood-glucose/last`,
  )

export const getObservationById = ({ readingId }: { readingId: string }) =>
  authenticatedFetch<BloodGlucoseObservationApiDto>(
    `${backendUrl}/v1/observations/blood-glucose/${readingId}`,
  )

export const getFormById = ({ formId }: { formId: string }) =>
  authenticatedFetch<BloodGlucoseFormApiDto>(`${backendUrl}/v1/forms/blood-glucose/${formId}`)

// Matches UpdateBloodGlucoseFormApiDtoV1 on the backend: all fields optional,
// no id/patientId/readingId/editable, and no dateTime (a form's dateTime is
// fixed at creation — for reading-backed forms by the reading, for orphan
// meal/quick-check forms by the patient at submit).
export type UpdateBloodGlucoseFormApiDto = Partial<
  Omit<BloodGlucoseFormApiDto, "id" | "patientId" | "readingId" | "editable" | "dateTime">
>

export const updateBloodGlucoseForm = ({
  formId,
  update,
}: {
  formId: string
  update: UpdateBloodGlucoseFormApiDto
}) =>
  authenticatedFetch<BloodGlucoseFormApiDto>(`${backendUrl}/v1/forms/blood-glucose/${formId}`, {
    method: "PATCH",
    body: JSON.stringify(update),
  })
